import { useRef, useState } from 'react';
import { Image, FileText, MapPin, BarChart2, Camera, Video, X } from 'lucide-react';

interface Props {
  isGroup?: boolean;
  sharingLocation?: boolean;
  visible?: boolean;
  onImage: (file: File) => void;
  onVideo: (file: File) => void;
  onDocument: (file: File) => void;
  onLocation: () => void;
  onPoll?: () => void;
  onClose: () => void;
}

export const AttachMenu = ({
  isGroup, sharingLocation, visible = false, onImage, onVideo, onDocument, onLocation, onPoll, onClose,
}: Props) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const docInputRef   = useRef<HTMLInputElement>(null);

  // Camera modal state
  const [showCamera, setShowCamera] = useState(false);
  const [cameraMode, setCameraMode] = useState<'photo' | 'video'>('photo');
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [stream, setStream]         = useState<MediaStream | null>(null);
  const [recording, setRecording]   = useState(false);
  const videoRef    = useRef<HTMLVideoElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef   = useRef<Blob[]>([]);

  const openCamera = async (mode: 'photo' | 'video') => {
    setCameraMode(mode);
    setShowCamera(true);
    onClose();
    await startStream('environment');
  };

  const startStream = async (facing: 'user' | 'environment') => {
    stream?.getTracks().forEach(t => t.stop());
    const s = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: cameraMode === 'video',
    });
    setStream(s);
    setFacingMode(facing);
    if (videoRef.current) videoRef.current.srcObject = s;
  };

  const flipCamera = () => startStream(facingMode === 'user' ? 'environment' : 'user');

  const takePhoto = () => {
    if (!videoRef.current || !stream) return;
    const canvas = document.createElement('canvas');
    canvas.width  = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d')?.drawImage(videoRef.current, 0, 0);
    canvas.toBlob(blob => {
      if (!blob) return;
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      onImage(file);
      closeCamera();
    }, 'image/jpeg', 0.92);
  };

  const startRecording = () => {
    if (!stream) return;
    chunksRef.current = [];
    const recorder = new MediaRecorder(stream);
    recorder.ondataavailable = e => chunksRef.current.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const file = new File([blob], `video_${Date.now()}.webm`, { type: 'video/webm' });
      onVideo(file);
      closeCamera();
    };
    recorder.start();
    recorderRef.current = recorder;
    setRecording(true);
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const closeCamera = () => {
    stream?.getTracks().forEach(t => t.stop());
    setStream(null);
    setShowCamera(false);
    setRecording(false);
  };

  const items = [
    {
      icon: <Image size={20} />,
      label: 'Imagen',
      color: '#7c3aed',
      action: () => { imageInputRef.current?.click(); },
    },
    {
      icon: <Camera size={20} />,
      label: 'Cámara',
      color: '#0ea5e9',
      action: () => openCamera('photo'),
    },
    {
      icon: <Video size={20} />,
      label: 'Video',
      color: '#ef4444',
      action: () => openCamera('video'),
    },
    {
      icon: <FileText size={20} />,
      label: 'Archivo',
      color: '#f59e0b',
      action: () => { docInputRef.current?.click(); },
    },
    {
      icon: <MapPin size={20} />,
      label: sharingLocation ? 'Detener ubicación' : 'Ubicación',
      color: sharingLocation ? '#ef4444' : '#22c55e',
      action: () => { onLocation(); onClose(); },
    },
    ...(isGroup && onPoll ? [{
      icon: <BarChart2 size={20} />,
      label: 'Encuesta',
      color: '#8b5cf6',
      action: () => { onPoll(); onClose(); },
    }] : []),
  ];

  return (
    <>
      {/* ── Attach menu ── */}
      <div className="attach-menu" style={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        marginBottom: '8px',
        display: visible ? 'flex' : 'none',
        background: 'var(--card-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: '16px',
        padding: '0.5rem',
        gap: '0.25rem',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        zIndex: 50,
        flexWrap: 'wrap',
        maxWidth: '260px',
      }}>
        {items.map(item => (
          <button
            key={item.label}
            onClick={item.action}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.3rem',
              padding: '0.6rem 0.75rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '12px',
              minWidth: '64px',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--input-bg)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: item.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff',
            }}>
              {item.icon}
            </div>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
              {item.label}
            </span>
          </button>
        ))}
      </div>

      {/* Hidden inputs */}
      <input ref={imageInputRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }}
        onChange={e => {
          Array.from(e.target.files || []).forEach(f => f.type.startsWith('video') ? onVideo(f) : onImage(f));
          onClose();
        }} />
      <input ref={docInputRef} type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.mp3,.mp4,.mov"
        multiple style={{ display: 'none' }}
        onChange={e => {
          Array.from(e.target.files || []).forEach(f => onDocument(f));
          onClose();
        }} />
      <input ref={videoInputRef} type="file" accept="video/*" style={{ display: 'none' }}
        onChange={e => { if (e.target.files?.[0]) onVideo(e.target.files[0]); }} />

      {/* ── Camera modal ── */}
      {showCamera && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: '#000',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Video preview */}
          <video ref={videoRef} autoPlay playsInline muted style={{
            flex: 1, objectFit: 'cover', width: '100%',
            transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
          }} />

          {/* Top bar */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            padding: '1rem',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)',
          }}>
            <button onClick={closeCamera} style={{
              background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff',
              borderRadius: '50%', width: 40, height: 40,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}>
              <X size={20} />
            </button>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setCameraMode(m => m === 'photo' ? 'video' : 'photo')}
                style={{
                  background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff',
                  borderRadius: '20px', padding: '0.4rem 0.9rem', cursor: 'pointer', fontSize: '0.8rem',
                }}
              >
                {cameraMode === 'photo' ? '📹 Video' : '📷 Foto'}
              </button>
            </div>
          </div>

          {/* Bottom controls */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            padding: '2rem 1rem calc(2rem + env(safe-area-inset-bottom, 0px))',
            display: 'flex', alignItems: 'center', justifyContent: 'space-around',
            background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
          }}>
            {/* Flip camera */}
            <button onClick={flipCamera} style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
              borderRadius: '50%', width: 48, height: 48,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              fontSize: '1.2rem',
            }}>
              🔄
            </button>

            {/* Shutter / Record */}
            {cameraMode === 'photo' ? (
              <button onClick={takePhoto} style={{
                width: 72, height: 72, borderRadius: '50%',
                background: '#fff', border: '4px solid rgba(255,255,255,0.5)',
                cursor: 'pointer',
              }} />
            ) : (
              <button onClick={recording ? stopRecording : startRecording} style={{
                width: 72, height: 72, borderRadius: '50%',
                background: recording ? '#ef4444' : '#fff',
                border: `4px solid ${recording ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.5)'}`,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {recording && <div style={{ width: 24, height: 24, borderRadius: '4px', background: '#fff' }} />}
              </button>
            )}

            {/* Spacer */}
            <div style={{ width: 48 }} />
          </div>

          {/* Recording indicator */}
          {recording && (
            <div style={{
              position: 'absolute', top: '5rem', left: '50%', transform: 'translateX(-50%)',
              background: 'rgba(239,68,68,0.8)', color: '#fff',
              padding: '0.3rem 0.8rem', borderRadius: '20px', fontSize: '0.8rem',
              display: 'flex', alignItems: 'center', gap: '0.4rem',
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', animation: 'pulse 1s infinite' }} />
              Grabando...
            </div>
          )}
        </div>
      )}
    </>
  );
};